import { ROUGHNESS, unit, type LaunchConditions } from '@openrocket-web/physics';
import type { Action, Simulation } from '../state/app';
import type { Analysis } from '../state/derived';
import { CheckField, NumberField, SelectField, TextField } from './Fields';
import type { Fmt } from './format';

interface Props {
  readonly sim: Simulation;
  readonly analysis: Analysis;
  readonly fmt: Fmt;
  readonly running: boolean;
  /** Configuraciones de motor entre las que elegir; la primera opción, vacía, sigue a la activa. */
  readonly configOptions: ReadonlyArray<{ readonly value: string; readonly label: string }>;
  readonly dispatch: (a: Action) => void;
  readonly onRun: () => void;
}

const ROUGHNESS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: String(ROUGHNESS.polished), label: 'Pulido (0,5 µm)' },
  { value: String(ROUGHNESS.smooth), label: 'Liso (10 µm)' },
  { value: String(ROUGHNESS.normal), label: 'Normal (50 µm)' },
  { value: String(ROUGHNESS.rough), label: 'Rugoso (150 µm)' },
  { value: String(ROUGHNESS.rawWood), label: 'Sin acabar (300 µm)' },
];

/** Errores del diseño que impiden simular. Se muestra en la pestaña de simulaciones y en el editor. */
export function BlockedNotice({ analysis }: { readonly analysis: Analysis }): JSX.Element | null {
  if (analysis.valid) return null;
  return (
    <div className="issue-box" role="alert">
      <p>
        <strong>No se puede simular:</strong> corrige estos errores del diseño.
      </p>
      <ul className="issue-list issue-list--error">
        {analysis.errors.map((e, i) => (
          <li key={i}>
            {e.componentId !== 'rocket' && <code>{e.componentId}</code>} {e.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Contenido del diálogo «Editar simulación»: nombre, lanzamiento, viento y modelo de una simulación de la lista. */
export function SimulationEditor({ sim, analysis, fmt, running, configOptions, dispatch, onRun }: Props): JSX.Element {
  const { launch, wind, sim: settings, id } = sim;
  const deg = unit('deg');
  const roughnessValue = String(settings.roughness);
  const roughnessOptions = ROUGHNESS_OPTIONS.some((o) => o.value === roughnessValue) ? ROUGHNESS_OPTIONS : [...ROUGHNESS_OPTIONS, { value: roughnessValue, label: `Equivalente del archivo (${(settings.roughness * 1e6).toFixed(0)} µm)` }];
  const setLaunch = (patch: Partial<LaunchConditions>): void => dispatch({ type: 'sim-launch', id, patch });

  return (
    <div className="sim-editor">
      <TextField label="Nombre de la simulación" value={sim.name} onCommit={(name) => dispatch({ type: 'sim-rename', id, name })} />

      <SelectField label="Configuración del motor" value={sim.configId ?? ''} options={configOptions} onCommit={(v) => dispatch({ type: 'sim-config', id, configId: v === '' ? undefined : v })} />

      <fieldset>
        <legend>Lanzamiento</legend>
        <NumberField label="Longitud de la varilla" unit={fmt.dist} value={launch.rodLength} onCommit={(v) => setLaunch({ rodLength: v })} />
        <NumberField label="Ángulo de la varilla respecto a la vertical" unit={deg} value={launch.rodAngle} onCommit={(v) => setLaunch({ rodAngle: v })} />
        <NumberField label="Acimut de la varilla (desde el norte)" unit={deg} value={launch.rodDirection} onCommit={(v) => setLaunch({ rodDirection: v })} />
        <NumberField label="Altitud del sitio" unit={fmt.dist} value={launch.siteAltitude} onCommit={(v) => setLaunch({ siteAltitude: v })} />
        <NumberField label="Temperatura" unit={fmt.temp} value={launch.temperature} onCommit={(v) => setLaunch({ temperature: v })} />
        <NumberField label="Presión" unit={fmt.pressure} value={launch.pressure} onCommit={(v) => setLaunch({ pressure: v })} />
      </fieldset>

      <fieldset>
        <legend>Viento</legend>
        <NumberField label="Velocidad media" unit={fmt.speed} value={wind.speed} onCommit={(v) => dispatch({ type: 'sim-wind', id, patch: { speed: v } })} />
        <NumberField label="Dirección de la que sopla (desde el norte)" unit={deg} value={wind.direction} onCommit={(v) => dispatch({ type: 'sim-wind', id, patch: { direction: v } })} />
        <NumberField label="Intensidad de turbulencia (0 a 1)" value={wind.turbulenceIntensity} onCommit={(v) => dispatch({ type: 'sim-wind', id, patch: { turbulenceIntensity: Math.min(Math.max(v, 0), 1) } })} />
      </fieldset>

      <fieldset>
        <legend>Modelo</legend>
        <SelectField label="Acabado de la superficie" value={roughnessValue} options={roughnessOptions} onCommit={(v) => dispatch({ type: 'sim-settings', id, patch: { roughness: Number(v) } })} />
        <NumberField label="Paso de tiempo (s)" value={settings.timeStep} hint="El paso real se adapta; este es el máximo" onCommit={(v) => dispatch({ type: 'sim-settings', id, patch: { timeStep: Math.max(v, 0.001) } })} />
        <NumberField label="Semilla aleatoria" integer value={settings.seed} onCommit={(v) => dispatch({ type: 'sim-settings', id, patch: { seed: Math.round(v) } })} />
        <CheckField label="Perturbación aleatoria pequeña de cabeceo y guiñada" checked={settings.perturbation} hint="Evita un vuelo perfectamente simétrico; con la misma semilla el resultado se repite" onCommit={(v) => dispatch({ type: 'sim-settings', id, patch: { perturbation: v } })} />
      </fieldset>

      <BlockedNotice analysis={analysis} />

      <div className="toolbar">
        <button type="button" className="primary" disabled={!analysis.valid || running} onClick={onRun}>
          Simular
        </button>
        {running && <span className="muted small">Hay una simulación en curso: espera a que termine o cancélala.</span>}
      </div>
    </div>
  );
}
